import { computed, toValue, type MaybeRefOrGetter } from 'vue';

import type {
  TransactionDetailsDialogDirection,
  TransactionDetailsDialogSource,
} from '../transaction-details.types';

export type TransactionDetailsActiveSelection = {
  transactionId: string;
  direction: TransactionDetailsDialogDirection;
  transactionSource: TransactionDetailsDialogSource;
};

interface UseTransactionDetailsSelectionOptions {
  visible: MaybeRefOrGetter<boolean>;
  transactionId: MaybeRefOrGetter<string | null | undefined>;
  direction: MaybeRefOrGetter<TransactionDetailsDialogDirection | null | undefined>;
  transactionSource: MaybeRefOrGetter<TransactionDetailsDialogSource | null | undefined>;
}

export const useTransactionDetailsSelection = ({
  visible,
  transactionId,
  direction,
  transactionSource,
}: UseTransactionDetailsSelectionOptions) => {
  const resolvedDirection = computed<TransactionDetailsDialogDirection | null>(() => {
    const currentDirection = toValue(direction);

    return currentDirection ?? null;
  });

  const resolvedTransactionId = computed(() => {
    return toValue(transactionId)?.trim() || null;
  });

  const resolvedTransactionSource = computed<TransactionDetailsDialogSource | null>(() => {
    const currentTransactionSource = toValue(transactionSource);
    if (currentTransactionSource) return currentTransactionSource;

    if (resolvedDirection.value === 'in') return 'INCOMING_TRANSFER';
    if (resolvedDirection.value === 'out') return 'OUTGOING_TRANSFER';

    return null;
  });

  const activeSelection = computed<TransactionDetailsActiveSelection | null>(() => {
    if (
      !toValue(visible) ||
      !resolvedTransactionId.value ||
      !resolvedDirection.value ||
      !resolvedTransactionSource.value
    ) {
      return null;
    }

    return {
      transactionId: resolvedTransactionId.value,
      direction: resolvedDirection.value,
      transactionSource: resolvedTransactionSource.value,
    };
  });

  return {
    resolvedDirection,
    resolvedTransactionId,
    resolvedTransactionSource,
    activeSelection,
  };
};
